import React, { useState, useCallback, useRef, useEffect } from "react";
import { FaCopy, FaEyeDropper } from "react-icons/fa";
import {
  changeFileColor,
  updateBackgroundColor,
} from "../redux/folder/folderSlice";
import { useDispatch } from "react-redux";
import { update_file_color_thunk } from "../redux/folder/folderThunk";

export function Colorpicker({
  color,
  onChange,
  isDragging,
  setIsDragging,
  hexValue,
  setHexValue,
  item,
  setShowColorPicker,
  closeContextMenu,
  changeBackgroundColor,
}) {
  const token = localStorage.getItem("todoToken");
  const [hsva, setHsva] = useState({ h: 0, s: 0, v: 100, a: 1 });
  const [tempHexValue, setTempHexValue] = useState(hexValue);
  const dispatch = useDispatch();
  const gradientRef = useRef(null);
  const hueRef = useRef(null);

  const commonColorChangeFun = (color, item) => {
    setTempHexValue(color);
    if (isDragging) {
      setHexValue(color);
      dispatch(updateBackgroundColor({ item, color }));
      dispatch(changeFileColor({ item, color }));
      onChange(color);
    }
  };

  // Sync `color` prop with internal state
  useEffect(() => {
    if (color) {
      const { h, s, v } = hexToHsv(color);
      setHsva({ h, s, v, a: 1 });
      commonColorChangeFun(color, item);
    }
  }, [color]);

  const handleGradientChange = useCallback(
    (e) => {
      const rect = gradientRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      const s = (x / rect.width) * 100;
      const v = 100 - (y / rect.height) * 100;

      const newColor = hsvToHex(hsva.h, s, v);
      commonColorChangeFun(newColor, item);
      setHsva((prev) => ({ ...prev, s, v }));
    },
    [hsva.h]
  );

  const handleHueChange = useCallback(
    (e) => {
      const rect = hueRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const hue = (x / rect.width) * 360;

      const newColor = hsvToHex(hue, hsva.s, hsva.v);
      commonColorChangeFun(newColor, item);
      setHsva((prev) => ({ ...prev, h: hue }));
    },
    [hsva.s, hsva.v]
  );

  const handleMouseDown = useCallback(
    (e, type) => {
      setIsDragging(true);
      if (type === "gradient") handleGradientChange(e);
      if (type === "hue") handleHueChange(e);
    },
    [handleGradientChange, handleHueChange]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
    commonColorChangeFun(tempHexValue, item);
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      if (gradientRef.current?.contains(e.target)) {
        handleGradientChange(e);
      } else if (hueRef.current?.contains(e.target)) {
        handleHueChange(e);
      }
    },
    [isDragging, handleGradientChange, handleHueChange]
  );

  // Attach event listeners for dragging
  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove]);

  const handleHexChange = useCallback((e) => {
    const newValue = e.target.value;
    commonColorChangeFun(newValue, item);
    if (/^#[0-9A-F]{6}$/i.test(newValue)) {
      const { h, s, v } = hexToHsv(newValue);
      setHsva({ h, s, v, a: 1 });
    }
  }, []);

  const handleDone = () => {
    onChange(hexValue); // Only call onChange here to avoid intermediate changes.
  };

  const handleCopyHex = () => {
    navigator.clipboard.writeText(hexValue);
    // alert("Hex color copied to clipboard!");
  };

  const handleEyeDropper = useCallback(async () => {
    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const newColor = result.sRGBHex;
      commonColorChangeFun(newColor, item);

      const { h, s, v } = hexToHsv(newColor);
      setHsva({ h, s, v, a: 1 });
    } catch (e) {
      console.log("EyeDropper not supported or user canceled");
      return;
    }
  }, [onChange]);

  const handleSaveColor = () => {
    let data = {
      id: item?.id,
      backgroundColor: tempHexValue,
    };
    dispatch(update_file_color_thunk({ data, token }));
    changeBackgroundColor(data, true);
    closeContextMenu();
    setShowColorPicker(false);
  };

  return (
    <div className="p-4 bg-white space-y-2 rounded-xl shadow-lg w-[300px]"
    style={{
      zIndex: 2147483647
    }}
    >
      {/* Gradient Picker */}
      <div
        ref={gradientRef}
        className="w-full h-[200px] rounded-xl mb-4 relative cursor-pointer"
        style={{
          background: `linear-gradient(to bottom, transparent, #000), 
                      linear-gradient(to right, #fff, hsl(${hsva.h}, 100%, 50%))`,
        }}
        onMouseDown={(e) => handleMouseDown(e, "gradient")}
      >
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${hsva.s}%`,
            top: `${100 - hsva.v}%`,
            backgroundColor: hexValue,
          }}
        />
      </div>

      {/* Hue Picker */}
      <div
        ref={hueRef}
        className="w-full h-4 rounded-full mb-4 cursor-pointer relative"
        style={{
          background:
            "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
        }}
        onMouseDown={(e) => handleMouseDown(e, "hue")}
      >
        <div
          className="absolute w-4 h-4 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 top-0"
          style={{
            left: `${(hsva.h / 360) * 100}%`,
            backgroundColor: `hsl(${hsva.h}, 100%, 50%)`,
          }}
        />
      </div>

      {/* Hex Input */}
      <div className="flex items-center gap-2  text-black">
        <div className="flex-1 relative ">
          <div
            className="absolute left-3 top-1/2  -translate-y-1/2 w-4 h-4 rounded-full border border-gray-200"
            style={{ backgroundColor: hexValue }}
          />
          <input
            type="text"
            value={hexValue}
            onChange={handleHexChange}
            className="w-full pl-9  pr-16 py-2 bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <FaCopy
          className="cursor-pointer text-gray-500"
          title="Copy Hex Code"
          onClick={handleCopyHex}
        />
        <FaEyeDropper
          className="cursor-pointer text-gray-500"
          title="Eyedropper Tool"
          onClick={handleEyeDropper}
        />
      </div>

      {/* Hex Code Label */}

      <button
        onClick={handleSaveColor}
        className="w-full py-2 px-4 bg-blue-500 text-white rounded-lg"
      >
        Done
      </button>
    </div>
  );
}

// Utility functions
function hsvToHex(h, s, v) {
  h = h % 360;
  s = s / 100;
  v = v / 100;

  const f = (n) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };

  const r = Math.round(f(5) * 255);
  const g = Math.round(f(3) * 255);
  const b = Math.round(f(1) * 255);

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function hexToHsv(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }

  return { h, s: s * 100, v: v * 100 };
}